import { useEffect, useState, type ReactElement } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import type { UseToastResult } from "@/hooks/useToast";
import { buildDhanAccount } from "@/lib/brokerAccounts";
import { dhanApi, type DhanLoginUrl, type DhanStatus } from "@/lib/dhanApi";
import { describeError } from "@/lib/http";
import { BrokerCard } from "./BrokerCards";
import { DhanPanel } from "./DhanPanel";
import { DhanTokenDialog } from "./DhanTokenDialog";

export const DHAN_STATUS_KEY = ["broker", "dhan", "status"] as const;
const POLL_MS = 30_000;

interface Props {
  now: Date;
  toast: UseToastResult;
}

type StatusMutation<V = void> = UseMutationResult<DhanStatus, unknown, V>;

interface Mutations {
  login: UseMutationResult<DhanLoginUrl, unknown, void>;
  paste: StatusMutation<string>;
  renew: StatusMutation;
  refresh: StatusMutation;
  logout: StatusMutation;
  busy: boolean;
}

/** The actions on the Dhan session; each writes the fresh status into the cache and toasts. */
function useDhanMutations(toast: UseToastResult, onPasted: () => void): Mutations {
  const qc = useQueryClient();
  const done =
    (message: string) =>
    (data: DhanStatus): void => {
      qc.setQueryData(DHAN_STATUS_KEY, data);
      toast.push("success", message);
    };
  const failed =
    (what: string) =>
    (err: unknown): void => {
      toast.push("error", `${what}: ${describeError(err)}`);
    };
  const login = useMutation({
    mutationFn: () => dhanApi.loginUrl(),
    // Dhan's own login page, as for Kite and Fyers: it brings the browser back connected.
    onSuccess: (data) => {
      window.location.href = data.login_url;
    },
    onError: failed("Dhan login failed"),
  });
  const paste = useMutation({
    mutationFn: (token: string) => dhanApi.pasteToken(token),
    onSuccess: (data) => {
      done("Connected to Dhan")(data);
      onPasted();
    },
    onError: failed("Dhan connect failed"),
  });
  const renew = useMutation({
    mutationFn: () => dhanApi.renew(),
    onSuccess: done("Dhan token renewed"),
    onError: failed("Renew failed"),
  });
  const refresh = useMutation({
    mutationFn: () => dhanApi.refresh(),
    onSuccess: done("Dhan status refreshed"),
    onError: failed("Refresh failed"),
  });
  const logout = useMutation({
    mutationFn: () => dhanApi.logout(),
    onSuccess: done("Disconnected from Dhan"),
    onError: failed("Dhan logout failed"),
  });
  const busy =
    login.isPending || paste.isPending || renew.isPending || refresh.isPending || logout.isPending;
  return { login, paste, renew, refresh, logout, busy };
}

/** Dhan sends the browser back to `/broker?dhan_connected=1` (or `?dhan_error=1`): say so once,
 * clear the address, and re-read the status. */
function useReturnFromDhan(toast: UseToastResult): void {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const connected = params.get("dhan_connected");
  const failed = params.get("dhan_error");
  useEffect(() => {
    if (connected === null && failed === null) return;
    if (connected === "1") toast.push("success", "Connected to Dhan");
    else toast.push("error", "Dhan login failed - the reason is on the Dhan card");
    setParams({}, { replace: true });
    void qc.invalidateQueries({ queryKey: DHAN_STATUS_KEY });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, failed]);
}

/** The Dhan card. Self-contained: it reads its own status and hides itself on a deployment with no
 * DHAN_CLIENT_ID. Connect sends the browser to Dhan's login when the API key and secret are set;
 * otherwise (and as a fallback) it takes a pasted 24-hour token. */
export function DhanSection({ now, toast }: Readonly<Props>): ReactElement | null {
  const [dialogOpen, setDialogOpen] = useState(false);
  const m = useDhanMutations(toast, () => {
    setDialogOpen(false);
  });
  useReturnFromDhan(toast);
  const status = useQuery({
    queryKey: DHAN_STATUS_KEY,
    queryFn: () => dhanApi.status(),
    refetchInterval: POLL_MS,
  });
  const data = status.data;
  if (data?.configured !== true) return null;
  return (
    <>
      <DhanCard
        status={data}
        now={now}
        m={m}
        onConnect={() => {
          if (data.login_enabled) m.login.mutate();
          else setDialogOpen(true);
        }}
        onPaste={() => {
          setDialogOpen(true);
        }}
      />
      <DhanTokenDialog
        open={dialogOpen}
        busy={m.paste.isPending}
        onClose={() => {
          setDialogOpen(false);
        }}
        onSubmit={(token) => {
          m.paste.mutate(token);
        }}
      />
    </>
  );
}

function DhanCard({
  status,
  now,
  m,
  onConnect,
  onPaste,
}: Readonly<{
  status: DhanStatus;
  now: Date;
  m: Mutations;
  onConnect: () => void;
  onPaste: () => void;
}>): ReactElement {
  return (
    <BrokerCard
      broker="Dhan"
      subtitle="DhanHQ API v2 · Failed Breakout"
      icon="ti-chart-arrows-vertical"
      accounts={[buildDhanAccount(status, [])]}
      now={now}
      busy={m.busy}
      onConnect={onConnect}
      onDisconnect={() => {
        m.logout.mutate();
      }}
      footer={
        <DhanPanel
          status={status}
          busy={m.busy}
          onRenew={() => {
            m.renew.mutate();
          }}
          onRefresh={() => {
            m.refresh.mutate();
          }}
          onPaste={onPaste}
        />
      }
    />
  );
}
